import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileSpreadsheet, FileText, MapPin, MessageSquarePlus, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAssistant } from "@/context/AssistantContext";
import { useApi } from "@/hooks/use-api";
import { downloadFile } from "@/lib/api";
import { Delta } from "@/components/common/Delta";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { TimeRangePicker, rangeToParams } from "@/components/common/TimeRangePicker";
import { ProductionChart, METRICS } from "@/components/charts/ProductionChart";
import { EventsTable } from "@/components/events/EventsTable";
import { EventDetailSheet } from "@/components/events/EventDetailSheet";
import { ServiceRequestDialog } from "@/components/service/ServiceRequestDialog";
import { RequestStatusBadge } from "@/components/common/SeverityBadge";
import { PlantMap } from "@/components/map/PlantMap";
import { LensMark } from "@/components/brand/ProximaLogo";
import { fmtDate, fmtDateTime, fmtDayTime, fmtFlow, fmtHours, fmtInt, fmtLevel, fmtMW, fmtMWh, fmtNOK, fmtNum, fmtPct, fmtPrice, fmtTime, toOslo } from "@/lib/format";
import { STATUS } from "@/lib/status";
import { cn } from "@/lib/utils";

/* The plant page is built around the situation at the plant: what is happening now, as one clear status line;
   then production as the main content with the current power in the chart's head; history as a quiet row;
   technical data further down. No equal-weight KPI boxes. */

const sameOsloDay = (a, b) => {
  if (!a || !b) return false;
  const x = toOslo(a);
  const y = toOslo(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};
const since = (ts, asOf) => (sameOsloDay(ts, asOf) ? `kl. ${fmtTime(ts)}` : fmtDayTime(ts));

/* one line that says what matters: state, since when, why */
const StatusLine = ({ plant, openEvents = [], asOf }) => {
  const stopEvent = openEvents.find((e) => e.production_stop && !e.ended_at) || openEvents.find((e) => !e.ended_at);
  const waiting = openEvents.filter((e) => e.ended_at).length;
  const startedAt = stopEvent?.started_at || plant.measured_at;
  const cfg = STATUS[plant.status] || STATUS.running;
  if (plant.status === "running") {
    return (
      <p className="mt-3 text-[16px] leading-snug text-muted-foreground" data-testid="plant-status-line">
        <span className="font-medium text-foreground">I drift</span> · <span className="metric">{fmtNum(plant.power_mw, plant.power_mw < 1 ? 2 : 1)}</span> av <span className="metric">{fmtNum(plant.capacity_mw, 1)}</span> MW · siste måling {since(plant.measured_at, asOf)}
        {waiting ? <span> · {waiting === 1 ? "1 hendelse venter på kvittering" : `${waiting} hendelser venter på kvittering`}</span> : null}
      </p>
    );
  }
  const word = plant.status === "reduced" ? "Redusert drift" : cfg.label;
  return (
    <p className={cn("mt-3 text-[17px] font-medium leading-snug", cfg.text)} data-testid="plant-status-line">
      {word} siden {since(startedAt, asOf)}
      {plant.status_reason ? <span> · {plant.status_reason}</span> : null}
      {plant.status === "reduced" ? (
        <span className="font-normal text-muted-foreground">
          {" "}· <span className="metric">{fmtNum(plant.power_mw, 1)}</span> av <span className="metric">{fmtNum(plant.capacity_mw, 1)}</span> MW
        </span>
      ) : null}
    </p>
  );
};

/* a quiet figure: label, value, one line of context */
const Fig = ({ label, value, unit, sub, testId, size = "md" }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[13.5px] text-muted-foreground">{label}</div>
    <div className="mt-1 flex items-baseline gap-1.5">
      <span className={cn("metric font-semibold leading-none tracking-[-0.02em] text-foreground", size === "md" ? "text-[22px]" : "text-[17px]")}>{value}</span>
      {unit ? <span className="text-[13.5px] font-medium text-muted-foreground">{unit}</span> : null}
    </div>
    {sub ? <div className="mt-1.5 text-[13px] leading-snug text-muted-foreground">{sub}</div> : null}
  </div>
);

const SummaryStat = ({ label, value, prev, invert = false, testId }) => {
  const delta = prev !== undefined && prev !== null && typeof value === "number" && prev ? ((value - prev) / prev) * 100 : null;
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="metric text-[16px] font-medium leading-none text-foreground">{typeof value === "number" ? value : value}</span>
        {delta !== null ? <Delta value={delta} invert={invert} className="text-[11.5px]" /> : null}
      </div>
    </div>
  );
};

const LiveRow = ({ label, value, sub, testId }) => (
  <div className="flex items-start justify-between gap-3 py-2.5" data-testid={testId}>
    <span className="text-[13.5px] text-muted-foreground">{label}</span>
    <span className="text-right">
      <span className="metric text-[14.5px] font-medium">{value}</span>
      {sub ? <div className="metric text-[12px] text-muted-foreground">{sub}</div> : null}
    </span>
  </div>
);

const Section = ({ title, aside, children, testId, className }) => (
  <section className={cn("min-w-0", className)} data-testid={testId}>
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="label-caps">{title}</h2>
      {aside}
    </div>
    <div className="mt-2">{children}</div>
  </section>
);

const Swatch = ({ color, dashed }) => <span aria-hidden="true" className={cn("inline-block h-0 w-4 border-t-2", dashed ? "border-dashed" : "border-solid")} style={{ borderColor: color }} />;

export default function PlantDetailPage() {
  const { id } = useParams();
  const { isDark } = useTheme();
  const { setPlantContext, openWith } = useAssistant();
  const [range, setRange] = useState({ range: "7d" });
  const [overlays, setOverlays] = useState([]);
  const [compare, setCompare] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const detail = useApi(`/plants/${id}`, {}, { refetchInterval: 120_000 });
  const tsParams = useMemo(() => ({ ...rangeToParams(range), compare }), [range, compare]);
  const ts = useApi(`/plants/${id}/timeseries`, tsParams, { keepPrevious: true });
  const events = useApi(`/plants/${id}/events`, { limit: 100 });
  const service = useApi(`/plants/${id}/service`);

  const plant = detail.data?.plant;
  const k = detail.data?.kpis;
  const openEvents = detail.data?.open_events || [];

  useEffect(() => {
    if (plant) setPlantContext({ id: plant.id, name: plant.name });
    return () => setPlantContext(null);
  }, [plant, setPlantContext]);

  const overlayMap = useMemo(() => Object.fromEntries(overlays.map((o) => [o, true])), [overlays]);
  const toggleOverlay = (key) => setOverlays((cur) => (cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]));

  const doExport = async (format) => {
    setExporting(true);
    try {
      const name = await downloadFile("/reports/export", { format, plant_id: id, ...rangeToParams(range), resolution: ts.data?.resolution });
      toast.success("Eksport klar", { description: name });
    } catch (err) {
      toast.error(err.message || "Eksport feilet");
    } finally {
      setExporting(false);
    }
  };

  if (detail.isError) return <ErrorState message="Kraftverket finnes ikke eller du har ikke tilgang." onRetry={detail.refetch} testId="plant-error" />;

  const summary = ts.data?.summary;
  const prevSummary = ts.data?.previous_summary;
  const levelPct = plant ? Math.min(100, Math.max(0, ((plant.level_m - plant.intake_level_min_m) / (plant.intake_level_max_m - plant.intake_level_min_m)) * 100)) : 0;
  const asOf = plant?.measured_at;
  const currentStop = openEvents.find((e) => e.production_stop && !e.ended_at) || openEvents.find((e) => !e.ended_at);
  const primary = isDark ? METRICS.power.colorDark : METRICS.power.color;
  const fall = (k?.energy_today_delta_pct ?? 0) < -2 || (plant && plant.status !== "running");
  const explain = () => {
    const period = ts.data ? `${fmtDayTime(ts.data.start)} – ${fmtDayTime(ts.data.end)}` : "valgt periode";
    const q = fall
      ? `Forklar produksjonsfallet ved ${plant.name} i perioden ${period}. Hent effekt time for time, vannføring og hendelsene i perioden, og skill tydelig mellom det som er dokumentert i dataene og mulige forklaringer.`
      : `Forklar utviklingen i produksjonen ved ${plant.name} i perioden ${period}. Hent effekt, vannføring og hendelser i perioden, og skill mellom det som er dokumentert i dataene og mulige forklaringer.`;
    openWith(q, { plantId: plant.id, plantName: plant.name, label: plant.name, newConversation: true });
  };

  return (
    <div className="space-y-10" data-testid="plant-detail-page">
      {/* Head: name, where and how big, and the one line that matters */}
      <header data-testid="plant-header">
        <Link to="/kraftverk" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="plant-back-link">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Kraftverk
        </Link>
        <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            {detail.isLoading ? (
              <>
                <Skeleton className="h-9 w-80" />
                <Skeleton className="mt-3 h-5 w-64" />
              </>
            ) : (
              <>
                <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.015em]" data-testid="plant-name">
                  {plant.name}
                </h1>
                <p className="mt-1 text-[15px] text-muted-foreground" data-testid="plant-meta">
                  {plant.municipality} · <span className="metric">{fmtNum(plant.capacity_mw, 1)}</span> MW installert · {plant.price_area}
                </p>
                <StatusLine plant={plant} openEvents={openEvents} asOf={asOf} />
              </>
            )}
          </div>
          {!detail.isLoading ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2 md:pt-1">
              {currentStop ? (
                <Button variant="outline" size="sm" className="h-9 px-3 text-[13.5px]" onClick={() => setSelectedEvent({ ...currentStop, plant_name: plant.name })} data-testid="plant-see-event">
                  Se hendelsen
                </Button>
              ) : null}
              <Button size="sm" className="h-9 gap-1.5 px-3 text-[13.5px]" onClick={() => setServiceOpen(true)} data-testid="kraftverk-kontakt-open">
                <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden="true" /> Kontakt Proxima
              </Button>
            </div>
          ) : null}
        </div>
      </header>

      {/* Production – the main content */}
      <section data-testid="plant-production">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <div className="label-caps">Produksjon</div>
            {detail.isLoading ? (
              <Skeleton className="mt-3 h-10 w-40" />
            ) : (
              <>
                <div className="mt-2 flex items-baseline gap-2" data-testid="plant-head-figure">
                  <span className={cn("metric text-[38px] font-semibold leading-none tracking-[-0.03em] sm:text-[40px]", plant.status === "stopped" || plant.status === "maintenance" ? "text-rose-700 dark:text-rose-300" : "text-foreground")}>{fmtNum(plant.power_mw, plant.power_mw < 1 && plant.power_mw > 0 ? 2 : 1)}</span>
                  <span className="text-[18px] font-medium text-muted-foreground">MW</span>
                </div>
                <div className="mt-2 text-[14.5px] text-muted-foreground" data-testid="plant-head-sub">
                  <span className="metric">{fmtNum(plant.utilisation_pct, 0)} %</span> av <span className="metric">{fmtNum(plant.capacity_mw, 1)}</span> MW installert · siste måling kl. <span className="metric">{fmtTime(asOf)}</span>
                </div>
              </>
            )}
          </div>
          {/* the controls, together: period · more measurements · export */}
          <div className="flex flex-wrap items-center gap-1.5">
            <TimeRangePicker value={range} onChange={setRange} testIdPrefix="historikk-range" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={cn("h-9 gap-1.5 px-2.5 text-[13.5px]", overlays.length || compare ? "text-foreground" : "text-muted-foreground")} data-testid="plant-more-measurements">
                  <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
                  Flere målinger{overlays.length + (compare ? 1 : 0) ? <span className="metric">· {overlays.length + (compare ? 1 : 0)}</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[260px]">
                <DropdownMenuLabel className="text-[12.5px] font-medium text-muted-foreground">Legg til i grafen</DropdownMenuLabel>
                {["flow", "level", "spot"].map((m) => (
                  <DropdownMenuCheckboxItem key={m} checked={overlays.includes(m)} onCheckedChange={() => toggleOverlay(m)} onSelect={(e) => e.preventDefault()} data-testid={`overlay-${m}`}>
                    <span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: isDark ? METRICS[m].colorDark : METRICS[m].color }} aria-hidden="true" />
                    {METRICS[m].label} <span className="ml-1 text-muted-foreground">{METRICS[m].unit}</span>
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem checked={compare} onCheckedChange={setCompare} onSelect={(e) => e.preventDefault()} data-testid="compare-toggle">
                  Sammenlign med forrige periode
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground" disabled={exporting} aria-label="Eksporter måleserien" data-testid="historikk-export-menu">
                  <Download className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => doExport("csv")} data-testid="historikk-export-csv">
                  <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> CSV (semikolon)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="historikk-export-xlsx">
                  <FileSpreadsheet className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mt-5" data-testid="plant-hero-chart">
          {ts.isLoading && !ts.data ? (
            <Skeleton className="h-[380px] w-full" />
          ) : ts.isError ? (
            <ErrorState message="Kunne ikke hente tidsserien." onRetry={ts.refetch} />
          ) : (
            <div className={cn("transition-opacity duration-200", ts.isFetching && "opacity-60")}>
              <ProductionChart series={ts.data.series} previous={compare ? ts.data.previous : null} resolution={ts.data.resolution} spanDays={ts.data.span_days} capacity={ts.data.capacity_mw} overlays={overlayMap} events={ts.data.events} isDark={isDark} height={380} />
            </div>
          )}
        </div>

        {ts.data ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-5 gap-y-1 text-[13px] text-muted-foreground" data-testid="plant-chart-note">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <span className="inline-flex items-center gap-2"><Swatch color={primary} /> {ts.data.resolution === "hour" ? "Effekt, MW" : "Produksjon, MWh per døgn"}</span>
              {compare ? <span className="inline-flex items-center gap-2"><Swatch color={isDark ? "hsl(205 20% 70%)" : "hsl(207 16% 50%)"} dashed /> Forrige periode</span> : null}
              {overlays.map((m) => (
                <span key={m} className="inline-flex items-center gap-2"><Swatch color={isDark ? METRICS[m].colorDark : METRICS[m].color} /> {METRICS[m].label}, {METRICS[m].unit}</span>
              ))}
              <span>
                {fmtDayTime(ts.data.start)} – {fmtDayTime(ts.data.end)} · {ts.data.events?.length ? `${ts.data.events.length} ${ts.data.events.length === 1 ? "hendelse" : "hendelser"} i perioden` : "ingen hendelser i perioden"}
              </span>
            </div>
            {plant ? (
              <Button variant="ghost" size="sm" className="-mr-2 h-8 gap-2 px-2 text-[13px] text-muted-foreground hover:text-foreground" onClick={explain} data-testid="plant-explain-button">
                <LensMark className="h-3.5 w-3.5" />
                {fall ? "Forklar produksjonsfallet" : "Forklar utviklingen"}
              </Button>
            ) : null}
          </div>
        ) : null}

        {summary ? (
          <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-5 border-t border-border pt-5 sm:grid-cols-3 xl:grid-cols-6" data-testid="plant-period-summary">
            <SummaryStat label="Produksjon i perioden" value={fmtMWh(summary.energy_mwh)} prev={undefined} testId="summary-energy" />
            <SummaryStat label="Snitt effekt" value={fmtMW(summary.avg_power_mw, 2)} testId="summary-avg-power" />
            <SummaryStat label="Maks effekt" value={fmtMW(summary.max_power_mw, 2)} testId="summary-max-power" />
            <SummaryStat label="Kapasitetsfaktor" value={fmtPct(summary.capacity_factor_pct, 0)} testId="summary-cf" />
            <SummaryStat label="Timer stanset" value={fmtHours(summary.hours_stopped)} testId="summary-stopped" />
            <SummaryStat label="Est. inntekt i perioden" value={fmtNOK(summary.revenue_nok)} testId="summary-revenue" />
          </div>
        ) : null}
        {compare && prevSummary ? (
          <p className="metric mt-3 text-[12.5px] text-muted-foreground" data-testid="plant-prev-summary">
            Forrige periode {fmtDayTime(prevSummary.start)} – {fmtDayTime(prevSummary.end)}: {fmtMWh(prevSummary.energy_mwh)} · {fmtHours(prevSummary.hours_stopped)} stanset · {fmtNOK(prevSummary.revenue_nok)}
          </p>
        ) : null}
      </section>

      {/* History – a quiet row */}
      {k ? (
        <section className="grid grid-cols-2 gap-x-8 gap-y-6 border-t border-border pt-6 sm:grid-cols-4" data-testid="plant-kpis">
          <Fig label="Produsert i dag" value={fmtNum(k.energy_today_mwh, k.energy_today_mwh < 100 ? 1 : 0)} unit="MWh" sub={k.energy_today_delta_pct !== null && k.energy_today_delta_pct !== undefined ? <span className="inline-flex items-baseline gap-1.5"><Delta value={k.energy_today_delta_pct} neutral className="text-[13px] text-foreground" /> mot i går</span> : null} testId="plant-kpi-today" />
          <Fig label="Siste 30 dager" value={fmtInt(k.energy_30d_mwh)} unit="MWh" sub={k.energy_30d_delta_pct !== null && k.energy_30d_delta_pct !== undefined ? <span className="inline-flex items-baseline gap-1.5"><Delta value={k.energy_30d_delta_pct} neutral className="text-[13px] text-foreground" /> mot forrige 30 dager</span> : null} testId="plant-kpi-30d" />
          <Fig label="Tilgjengelighet, 30 d" value={fmtNum(k.availability_30d_pct, 1)} unit="%" sub={<><span className="metric">{fmtHours(k.hours_stopped_30d)}</span> stanset · kapasitetsfaktor <span className="metric">{fmtPct(k.capacity_factor_30d_pct, 0)}</span></>} testId="plant-kpi-availability" />
          <Fig label="Est. inntekt, 30 d" value={fmtNOK(k.revenue_30d_nok)} sub={<>Snittpris <span className="metric">{fmtPrice(k.avg_spot_30d_nok_mwh)}</span></>} testId="plant-kpi-revenue" />
        </section>
      ) : null}

      <div className="grid gap-10 border-t border-border pt-8 lg:grid-cols-12 lg:gap-14">
        <div className="min-w-0 lg:col-span-8">
          <Tabs defaultValue="events">
            <TabsList className="tabs-line">
              <TabsTrigger value="events" data-testid="plant-tab-events">
                Hendelser {events.data ? <span className="metric ml-1.5 text-[12.5px] text-muted-foreground">{events.data.events.length}</span> : null}
              </TabsTrigger>
              <TabsTrigger value="service" data-testid="plant-tab-service">
                Service
              </TabsTrigger>
              <TabsTrigger value="specs" data-testid="plant-tab-specs">
                Teknisk informasjon
              </TabsTrigger>
            </TabsList>
            <TabsContent value="events" className="mt-3">
              {events.isLoading ? (
                <Skeleton className="h-40" />
              ) : events.data?.events?.length ? (
                <EventsTable events={events.data.events} showPlant={false} onSelect={setSelectedEvent} testId="plant-events-table" />
              ) : (
                <EmptyState title="Ingen hendelser registrert" description="Alarmer, utfall og vedlikehold vises her." />
              )}
            </TabsContent>
            <TabsContent value="service" className="mt-4 space-y-8">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-[15px] font-semibold">Henvendelser til Proxima</h3>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 text-[13px]" onClick={() => setServiceOpen(true)} data-testid="plant-service-new-request">
                    <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden="true" /> Ny henvendelse
                  </Button>
                </div>
                {service.data?.requests?.length ? (
                  <ul className="mt-2 divide-y divide-border" data-testid="plant-service-requests">
                    {service.data.requests.map((r) => (
                      <li key={r.id} className="flex items-start justify-between gap-3 py-2.5">
                        <div className="min-w-0">
                          <div className="text-[14px] font-medium">{r.subject}</div>
                          <div className="metric mt-0.5 text-[12.5px] text-muted-foreground">
                            {r.ref} · {fmtDateTime(r.created_at)} · {r.created_by_name}
                          </div>
                        </div>
                        <RequestStatusBadge status={r.status} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-[14px] text-muted-foreground">Ingen henvendelser registrert for dette kraftverket.</p>
                )}
              </div>
              <div>
                <h3 className="text-[15px] font-semibold">Servicehistorikk</h3>
                {service.isLoading ? (
                  <Skeleton className="mt-3 h-32 w-full" />
                ) : service.data?.records?.length ? (
                  <Table className="mt-2" data-testid="plant-service-records">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="pl-0">Dato</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="hidden md:table-cell">Beskrivelse</TableHead>
                        <TableHead>Tekniker</TableHead>
                        <TableHead className="pr-0 text-right">Rapport</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {service.data.records.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="metric whitespace-nowrap pl-0 text-[13.5px]">{fmtDate(r.date)}</TableCell>
                          <TableCell className="text-[14px] font-medium">{r.type}</TableCell>
                          <TableCell className="hidden max-w-md text-[13.5px] text-muted-foreground md:table-cell">{r.description}</TableCell>
                          <TableCell className="text-[13.5px]">{r.technician}</TableCell>
                          <TableCell className="metric pr-0 text-right text-[13px] text-muted-foreground">{r.report_ref}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <EmptyState title="Ingen servicehistorikk" className="mt-3" />
                )}
              </div>
            </TabsContent>
            <TabsContent value="specs" className="mt-4">
              {plant ? (
                <dl className="grid gap-x-10 gap-y-0 text-[14px] sm:grid-cols-2" data-testid="plant-specs">
                  {[
                    ["Installert effekt", fmtMW(plant.capacity_mw, 2)],
                    ["Turbintype", `${plant.turbine_type} · ${plant.units} aggregat`],
                    ["Fallhøyde", `${plant.head_m} m`],
                    ["Slukeevne", fmtFlow(plant.design_flow_m3s)],
                    ["Minste driftsvannføring", fmtFlow(plant.min_turbine_flow_m3s)],
                    ["Inntak LRV / HRV", `${fmtNum(plant.intake_level_min_m, 1)} / ${fmtNum(plant.intake_level_max_m, 1)} moh`],
                    ["Forventet årsproduksjon", `${fmtNum(plant.annual_estimate_gwh, 1)} GWh`],
                    ["Prisområde", plant.price_area],
                    ["Idriftsatt", plant.commissioned_year],
                    ["Kommune", `${plant.municipality}, ${plant.county}`],
                    ["Driftssystem-ID", plant.hydroscada_tag],
                    ["Datakilde", plant.status_source],
                  ].map(([l, v]) => (
                    <div key={l} className="flex items-start justify-between gap-4 border-b border-border py-2.5">
                      <dt className="text-muted-foreground">{l}</dt>
                      <dd className="metric text-right font-medium">{v}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">{plant?.description}</p>
            </TabsContent>
          </Tabs>
        </div>

        <div className="min-w-0 space-y-9 lg:col-span-4">
          <Section title="Nå-verdier" aside={plant ? <span className="text-[12.5px] text-muted-foreground">Målt kl. <span className="metric">{fmtTime(asOf)}</span></span> : null} testId="plant-live-panel">
            {detail.isLoading ? (
              <Skeleton className="h-48" />
            ) : (
              <div className="divide-y divide-border">
                <LiveRow label="Vannføring i elv" value={fmtFlow(plant.flow_m3s)} sub={`Slukeevne ${fmtFlow(plant.design_flow_m3s)}`} testId="live-flow" />
                <LiveRow label="Gjennom turbin" value={fmtFlow(plant.turbine_flow_m3s)} testId="live-turbine-flow" />
                <div className="py-2.5" data-testid="live-level">
                  <div className="flex items-center justify-between">
                    <span className="text-[13.5px] text-muted-foreground">Vannstand inntak</span>
                    <span className="metric text-[14.5px] font-medium">{fmtLevel(plant.level_m)}</span>
                  </div>
                  <div className="util-track mt-2.5">
                    <div className="util-fill bg-chart-3" style={{ width: `${levelPct}%` }} />
                  </div>
                  <div className="metric mt-1.5 flex justify-between text-[11.5px] text-muted-foreground">
                    <span>LRV {fmtNum(plant.intake_level_min_m, 1)}</span>
                    <span>HRV {fmtNum(plant.intake_level_max_m, 1)}</span>
                  </div>
                </div>
                <LiveRow label={`Spotpris ${plant.price_area}`} value={fmtPrice(plant.spot_nok_mwh)} sub={`${fmtInt(plant.spot_nok_mwh)} NOK/MWh`} testId="live-spot" />
                <LiveRow label="Produsert i dag" value={fmtMWh(plant.energy_today_mwh)} sub={fmtNOK(plant.revenue_today_nok)} testId="live-today" />
              </div>
            )}
          </Section>

          {openEvents.length ? (
            <Section title="Åpne hendelser" aside={<span className="text-[12.5px] text-muted-foreground">{openEvents.length} venter på oppfølging</span>} testId="plant-open-events">
              <ul className="divide-y divide-border">
                {openEvents.map((e) => (
                  <li key={e.id}>
                    <button type="button" onClick={() => setSelectedEvent({ ...e, plant_name: plant.name })} className="-mx-2 flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-left transition-[background-color] duration-100 hover:bg-muted/60" data-testid={`plant-open-event-${e.id}`}>
                      <span className={cn("status-dot mt-[7px]", e.severity === "critical" ? "bg-rose-500" : e.severity === "warning" ? "bg-amber-500" : "bg-slate-400")} aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-medium">{e.title}</div>
                        <div className="metric mt-0.5 text-[12.5px] text-muted-foreground">
                          {fmtDayTime(e.started_at)}{e.ended_at ? ` – ${fmtTime(e.ended_at)} · avsluttet` : " · pågår"} · {e.status === "open" ? "ikke kvittert" : "kvittert"}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title="Siste service" testId="plant-last-service">
            {detail.data?.last_service ? (
              <div>
                <div className="text-[14px] font-medium">{detail.data.last_service.type}</div>
                <div className="metric mt-0.5 text-[12.5px] text-muted-foreground">
                  {fmtDate(detail.data.last_service.date)} · {detail.data.last_service.technician} · {detail.data.last_service.report_ref}
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">{detail.data.last_service.description}</p>
              </div>
            ) : (
              <p className="text-[13.5px] text-muted-foreground">Ingen service registrert.</p>
            )}
          </Section>

          {plant ? (
            <Section title="Beliggenhet" testId="plant-map-card">
              <div className="overflow-hidden rounded-md border border-border">
                <PlantMap plants={[plant]} height={170} interactive={false} zoomControl={false} />
              </div>
              <div className="metric mt-2 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                <MapPin className="h-3 w-3" aria-hidden="true" />
                {plant.municipality}, {plant.county} · {fmtNum(plant.lat, 3)}° N, {fmtNum(plant.lon, 3)}° Ø
              </div>
            </Section>
          ) : null}
        </div>
      </div>

      <EventDetailSheet event={selectedEvent ? { ...selectedEvent, plant_name: selectedEvent.plant_name || plant?.name } : null} open={!!selectedEvent} onOpenChange={(o) => !o && setSelectedEvent(null)} showPlantLink={false} onUpdated={(e) => { setSelectedEvent(e); events.refetch(); detail.refetch(); ts.refetch(); }} />
      {plant ? <ServiceRequestDialog open={serviceOpen} onOpenChange={setServiceOpen} plant={plant} onCreated={() => service.refetch()} /> : null}
    </div>
  );
}
